import { centsToFormattedDollars } from '@fruitful/fruitils';
import {
    Account,
    type BankAccountDisplayDetail,
    formatIncomeSplitPercentages,
    formatIncomeSplitPercentagesNew,
    type IncomeSplitConfig,
    type IncomeSplitConfigLegacy,
    IncomeSplitDetails,
    IncomeSplitRulesList,
    IncomeSplitRulesListNew,
    PaycheckRoutingStatus,
} from '@fruitful/income-split';
import IncomeSplitEndOfRules from '@fruitful/income-split/src/components/IncomeSplitEndOfRules';
import { Badge } from 'primereact/badge';
import { Column } from 'primereact/column';
import { DataTable } from 'primereact/datatable';
import React, { useMemo, useState } from 'react';
import {
    BodyLargeEmphasized,
    BodyMediumEmphasized,
    EyeIcon,
    Separator,
    Spinner,
    XStack,
    YStack,
} from 'smoothie';

import useGetAccountsForMembership from '../../../../banking/banking/hooks/useGetAccountsForMembership';
import { GroveSimpleModal } from '../../../../common/components/GroveSimpleModal';
import { formatDollars } from '../../../../common/utils/formatting';
import { PAYCHECK_STATUS_ORDER } from '../../../constants';
import {
    type IncomeSplitHistoryRow,
    type MembershipDetails,
    type PaycheckRoutingRow,
} from '../../../entityTypes';
import {
    findMatchingRoutingConfigForPaycheck,
    transformConfigs,
} from '../../membershipUtils';
import IncomeSplitHistoryModal from './IncomeSplitHistoryModal';
import PaycheckSplitRulesModal from './PaycheckSplitRulesModal';

interface IncomeSplitTabPanelProps {
    membershipDetails: MembershipDetails;
    showIncomeSplit: boolean;
}

export const IncomeSplitTabPanel = ({
    membershipDetails,
    showIncomeSplit,
}: IncomeSplitTabPanelProps) => {
    const [isPaycheckModalOpen, setIsPaycheckModalOpen] = useState(false);
    const [isIncomeSplitHistoryModalOpen, setIsIncomeSplitHistoryModalOpen] =
        useState(false);
    const [selectedPaycheckRoutingRow, setSelectedPaycheckRoutingRow] =
        useState<PaycheckRoutingRow | null>(null);
    const [selectedIncomeSplitHistoryRow, setSelectedIncomeSplitHistoryRow] =
        useState<IncomeSplitHistoryRow | null>(null);
    const [isAccountDetailsOpen, setIsAccountDetailsOpen] = useState(false);

    const {
        paychecks,
        paycheckRoutingConfigs,
        holisticSummary,
        incomeSplitConfig: tiedRulesIncomeSplitConfig,
        incomeSplitHistory,
    } = membershipDetails;
    const transformedConfigs = transformConfigs(paycheckRoutingConfigs);
    const holisticAccounts = Object.values(holisticSummary ?? {}).flatMap(
        (section) => section.accounts ?? [],
    );

    const tiedRulesIncomeSplitStatus =
        tiedRulesIncomeSplitConfig?.status ?? PaycheckRoutingStatus.DELETED;

    const incomeSplit = showIncomeSplit
        ? (paycheckRoutingConfigs[
              paycheckRoutingConfigs.length - 1
          ] as unknown as IncomeSplitConfig)
        : null;
    const legacyIncomeSplitStatus =
        (incomeSplit?.status as PaycheckRoutingStatus) ??
        PaycheckRoutingStatus.DELETED;

    const bankAccounts =
        holisticAccounts as unknown as BankAccountDisplayDetail[];
    const { data: depositAccounts, isLoading: isLoadingDepositAccounts } =
        useGetAccountsForMembership({
            membershipId: membershipDetails.id,
        });

    const externalAccounts = (depositAccounts ?? []).filter(
        (account) => account.isExternal,
    );

    const incomeSplitSourceAccountIds = new Set(
        (incomeSplit?.accounts ?? []).map((account) => account.accountId),
    );
    const sourceAccounts = holisticAccounts.filter((account) =>
        incomeSplitSourceAccountIds.has(account.bankAccountId ?? ''),
    );

    const membershipAccounts = useMemo(() => {
        return Object.values(holisticSummary ?? {})
            .flat()
            .map((category) => category.accounts)
            .flat();
    }, [holisticSummary]);

    const paychecksData = paychecks.map((paycheck): PaycheckRoutingRow => {
        const { originator, receiver, accountId, amount } = paycheck;

        const matchedConfig = findMatchingRoutingConfigForPaycheck(
            paycheck,
            transformedConfigs,
        );

        const matchedUser = membershipDetails.users.find(
            (user) => user.id === receiver,
        );

        const matchedAccount = membershipAccounts.find(
            (account) => account.bankAccountId === accountId,
        );

        return {
            originator,
            receiver: `${matchedUser?.firstName} ${matchedUser?.lastName}`,
            institution: matchedAccount?.institutionName ?? 'N/A',
            account: matchedAccount?.name ?? 'N/A',
            amount: formatDollars(amount / 100),
            status: matchedConfig ? matchedConfig.status : 'ELIGIBLE',
            rules: matchedConfig ? matchedConfig.rules : null,
        };
    });

    paychecksData.sort((a, b) => {
        return (
            PAYCHECK_STATUS_ORDER.indexOf(a.status) -
            PAYCHECK_STATUS_ORDER.indexOf(b.status)
        );
    });

    const incomeSplitHistoryData = useMemo(() => {
        return (incomeSplitHistory ?? [])
            .sort(
                (a, b) =>
                    new Date(b.transactionDate).getTime() -
                    new Date(a.transactionDate).getTime(),
            )
            .map<IncomeSplitHistoryRow>((row) => {
                const {
                    transactionDate,
                    accountId,
                    transactionAmount,
                    transactionDescription,
                    emailCopy,
                } = row;

                const matchedAccount = membershipAccounts.find(
                    (account) => account.bankAccountId === accountId,
                );

                return {
                    date: new Date(transactionDate).toLocaleString(),
                    account: matchedAccount?.name ?? 'N/A',
                    amount: centsToFormattedDollars(transactionAmount),
                    description: transactionDescription,
                    emailCopy,
                };
            });
    }, [incomeSplitHistory]);

    const status = tiedRulesIncomeSplitConfig
        ? tiedRulesIncomeSplitStatus
        : legacyIncomeSplitStatus;

    if (isLoadingDepositAccounts) return <Spinner />;

    return (
        <>
            {(incomeSplit ?? tiedRulesIncomeSplitConfig) ? (
                <XStack gap={16}>
                    <YStack gap={16} flexBasis="40%">
                        <BodyLargeEmphasized>
                            Income Split Configuration
                        </BodyLargeEmphasized>
                        <IncomeSplitDetails
                            incomeSplit={
                                tiedRulesIncomeSplitConfig ??
                                (incomeSplit as unknown as IncomeSplitConfigLegacy)
                            }
                            totalEligibleAccounts={
                                // TODO: modify isAccountValidAsIncomeSplitSource to work with memberships, so we can properly show 'All' in the copy like we do for the member app
                                sourceAccounts as unknown as BankAccountDisplayDetail[]
                            }
                            icon={EyeIcon}
                            statusBadge={
                                <Badge
                                    value={status}
                                    severity={
                                        status === PaycheckRoutingStatus.ACTIVE
                                            ? 'success'
                                            : 'danger'
                                    }
                                />
                            }
                            onIconPress={() => {
                                setIsAccountDetailsOpen(true);
                            }}
                            verifyNumberOfAccountsWatched={false}
                        />

                        {tiedRulesIncomeSplitConfig ? (
                            <>
                                <IncomeSplitRulesListNew
                                    incomeSplit={formatIncomeSplitPercentagesNew(
                                        tiedRulesIncomeSplitConfig,
                                    )}
                                    bankAccountDetails={bankAccounts}
                                    externalAccounts={externalAccounts}
                                />
                                <IncomeSplitEndOfRules />
                            </>
                        ) : (
                            <IncomeSplitRulesList
                                incomeSplit={formatIncomeSplitPercentages(
                                    incomeSplit as unknown as IncomeSplitConfigLegacy,
                                )}
                                bankAccountDetails={bankAccounts}
                                externalAccounts={externalAccounts}
                            />
                        )}
                        <GroveSimpleModal
                            isOpen={isAccountDetailsOpen}
                            onClose={() => {
                                setIsAccountDetailsOpen(false);
                            }}
                        >
                            <BodyMediumEmphasized
                                textAlign="center"
                                paddingBottom={16}
                            >
                                Income Accounts
                            </BodyMediumEmphasized>
                            <YStack
                                borderRadius={16}
                                backgroundColor="$gray200"
                                separator={<Separator borderColor="$gray500" />}
                            >
                                {sourceAccounts.map((account, i) => (
                                    <Account account={account} key={i} />
                                ))}
                            </YStack>
                        </GroveSimpleModal>
                    </YStack>
                    <YStack flexBasis="60%">
                        <BodyLargeEmphasized>
                            Income Split History
                        </BodyLargeEmphasized>
                        <DataTable
                            value={incomeSplitHistoryData}
                            tableStyle={{ width: '100%' }}
                            size="small"
                            emptyMessage="No income split history yet"
                            showHeaders={incomeSplitHistoryData.length > 0}
                            selectionMode="single"
                            onRowSelect={(e: {
                                data: IncomeSplitHistoryRow;
                            }) => {
                                const { data } = e;
                                setSelectedIncomeSplitHistoryRow(data);
                                setIsIncomeSplitHistoryModalOpen(true);
                            }}
                        >
                            <Column field="date" header="Date" />
                            <Column field="account" header="Account" />
                            <Column field="amount" header="Amount" />
                            <Column field="description" header="Description" />
                        </DataTable>
                        <GroveSimpleModal
                            isOpen={isIncomeSplitHistoryModalOpen}
                            onClose={() => {
                                setIsIncomeSplitHistoryModalOpen(false);
                            }}
                        >
                            <IncomeSplitHistoryModal
                                incomeSplitHistory={
                                    selectedIncomeSplitHistoryRow
                                }
                            />
                        </GroveSimpleModal>
                    </YStack>
                </XStack>
            ) : (
                <>
                    <DataTable
                        value={paychecksData}
                        tableStyle={{ width: '100%' }}
                        size="small"
                        emptyMessage={
                            showIncomeSplit
                                ? 'No detected income'
                                : 'No detected paychecks'
                        }
                        showHeaders={paychecksData.length > 0}
                        selectionMode="single"
                        onRowSelect={(e: { data: PaycheckRoutingRow }) => {
                            const { data } = e;
                            setSelectedPaycheckRoutingRow(data);
                            setIsPaycheckModalOpen(true);
                        }}
                    >
                        <Column field="originator" header="Originator" />
                        <Column field="receiver" header="Receiver" />
                        <Column field="institution" header="Institution" />
                        <Column field="account" header="Account" />
                        <Column field="amount" header="Amount" />
                        <Column field="status" header="Status" />
                    </DataTable>
                    <GroveSimpleModal
                        isOpen={isPaycheckModalOpen}
                        onClose={() => {
                            setIsPaycheckModalOpen(false);
                        }}
                    >
                        <PaycheckSplitRulesModal
                            paycheckSplitDetails={selectedPaycheckRoutingRow}
                            membershipAccounts={membershipAccounts}
                        />
                    </GroveSimpleModal>
                </>
            )}
        </>
    );
};
